import { orgAPI } from "@/api/api";
import { useSkills } from "@/api/skills";
import { SkillNameConflictDialog } from "@/components/SkillNameConflictDialog/SkillNameConflictDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ROUTES } from "@/constants/routes";
import {
  isDragEnterBubbledFromDescendant,
  isDragLeavingToDescendant,
} from "@/dnd/osFileDrop";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import {
  normalizeMarkdownForFrontmatterParse,
  unquoteYamlScalar,
} from "@/utils/skillMdFrontmatter";
import {
  generateUniqueSkillName,
  isSkillNameTaken,
} from "@/utils/skillNameConflict";
import {
  type FileSystemDirectoryHandleLike,
  traverseDataTransferItems,
  traverseDirectoryHandle,
} from "@/utils/traverseDataTransferItems";
import { uploadPresignedSkillFile } from "@/utils/uploadSkillFile";
import { Skill } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import JSZip from "jszip";
import {
  AlertCircle,
  CheckCircle2,
  FileArchive,
  FileText,
  Folder,
  Upload,
} from "lucide-react";
import mime from "mime-types";
import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// The server (skill-imports endpoints) is authoritative for all structure and
// frontmatter rules — validation here is fast-fail UX only, mirroring the
// server's checks so most problems surface before any upload starts.

const OS_JUNK_PATTERNS = [
  /^__MACOSX\//,
  /\/__MACOSX\//,
  /(^|\/)\.[^/]*$/, // dotfiles (.DS_Store, ._resource-forks, .gitignore, …)
  /(^|\/)Thumbs\.db$/i,
  /(^|\/)desktop\.ini$/i,
];

function isOSJunkFile(filePath: string): boolean {
  return OS_JUNK_PATTERNS.some((pattern) => pattern.test(filePath));
}

const MAX_IMPORT_FILES = 200;
const MAX_IMPORT_TOTAL_BYTES = 50 * 1024 * 1024;

/** One file collected from any source, keyed by the path sent in the manifest. */
interface UploadEntry {
  path: string;
  size: number;
  blob: () => Promise<Blob>;
}

type UploadSource =
  | { kind: "zip"; label: string; sizeLabel: string }
  | { kind: "md"; label: string; sizeLabel: string }
  | { kind: "folder"; label: string; sizeLabel: string };

interface ValidationResult {
  valid: boolean;
  errors: string[];
  /** Preview only — the server re-derives both at finalize. */
  skillName: string;
  description: string;
  fileCount: number;
  entries: UploadEntry[];
}

function parseFrontmatterPreview(skillMdText: string): {
  name: string;
  description: string;
} {
  const content = normalizeMarkdownForFrontmatterParse(skillMdText);
  const fmMatch = content.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!fmMatch) return { name: "", description: "" };
  const nameMatch = fmMatch[1].match(/^name:\s*(.+)$/m);
  const descMatch = fmMatch[1].match(/^description:\s*(.+)$/m);
  return {
    name: nameMatch ? unquoteYamlScalar(nameMatch[1]) : "",
    description: descMatch ? unquoteYamlScalar(descMatch[1]) : "",
  };
}

/** Shared structure checks over collected entries + the SKILL.md text. */
function validateEntries(
  entries: UploadEntry[],
  skillMdText: string | null,
): ValidationResult {
  const errors: string[] = [];

  if (entries.length === 0) {
    errors.push(
      "The selection contains no files (after filtering OS metadata).",
    );
  }
  if (entries.length > MAX_IMPORT_FILES) {
    errors.push(`A skill can contain at most ${MAX_IMPORT_FILES} files.`);
  }
  const totalBytes = entries.reduce((sum, e) => sum + e.size, 0);
  if (totalBytes > MAX_IMPORT_TOTAL_BYTES) {
    errors.push("The selection is larger than the 50 MB total limit.");
  }

  // Mirror the server's root rule: the shortest SKILL.md's directory is the
  // skill root and every file must live under it (finalize 400s otherwise).
  const skillMdPath = findSkillMdPath(entries.map((e) => e.path));
  if (skillMdPath) {
    const root = skillMdPath.slice(0, skillMdPath.lastIndexOf("/") + 1);
    const outside = entries.filter((e) => !e.path.startsWith(root));
    if (outside.length > 0) {
      errors.push(
        `All files must be inside the skill folder "${root}" — ` +
          `${outside.length} ${outside.length === 1 ? "file is" : "files are"} outside it (e.g. "${outside[0].path}").`,
      );
    }
  }

  let preview = { name: "", description: "" };
  if (skillMdText == null) {
    errors.push(
      "Missing SKILL.md — the selection must contain a SKILL.md file with frontmatter.",
    );
  } else {
    preview = parseFrontmatterPreview(skillMdText);
    if (!preview.name || !preview.description) {
      errors.push(
        "SKILL.md must start with frontmatter containing name and description fields.",
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    skillName: preview.name,
    description: preview.description,
    fileCount: entries.length,
    entries,
  };
}

/** The shortest SKILL.md path — its directory is the skill root. */
function findSkillMdPath(paths: string[]): string | null {
  const candidates = paths.filter((p) => /(^|\/)SKILL\.md$/i.test(p));
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.length - b.length);
  return candidates[0];
}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`;
}

function SkillUploadDropzone({
  disabled,
  source,
  onFilesSelected,
  onFolderSelected,
  onDropItems,
}: {
  disabled: boolean;
  source: UploadSource | null;
  onFilesSelected: (files: File[]) => void;
  onFolderSelected: (
    files: { relativePath: string; file: File }[],
    label: string,
  ) => void;
  onDropItems: (items: DataTransferItem[], files: File[]) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  // A native folder-mode dialog where available (Chromium, incl. the
  // desktop app); Safari/Firefox fall back to the webkitdirectory input.
  const pickFolder = async () => {
    const picker = (
      window as unknown as {
        showDirectoryPicker?: () => Promise<FileSystemDirectoryHandleLike>;
      }
    ).showDirectoryPicker;
    if (!picker) {
      folderInputRef.current?.click();
      return;
    }
    try {
      const dir = await picker.call(window);
      const files = await traverseDirectoryHandle(dir);
      if (files.length > 0) onFolderSelected(files, dir.name);
    } catch {
      // AbortError — the user cancelled the dialog.
    }
  };

  const SourceIcon =
    source?.kind === "zip"
      ? FileArchive
      : source?.kind === "folder"
        ? Folder
        : FileText;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "w-full rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
        isDragActive && "border-primary bg-muted/40",
        !isDragActive && "hover:border-primary/50 hover:bg-muted/30",
      )}
      onClick={() => !disabled && fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!disabled) fileInputRef.current?.click();
        }
      }}
      onDragEnter={(e) => {
        if (disabled || !e.dataTransfer.types.includes("Files")) return;
        if (isDragEnterBubbledFromDescendant(e.nativeEvent)) return;
        e.preventDefault();
        dragDepthRef.current += 1;
        setIsDragActive(true);
      }}
      onDragOver={(e) => {
        if (disabled || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (isDragLeavingToDescendant(e.nativeEvent)) return;
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragActive(false);
      }}
      onDrop={(e) => {
        if (disabled || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDragActive(false);
        // DataTransferItem references die once the handler yields — snapshot
        // them synchronously (hard requirement of traverseDataTransferItems).
        onDropItems(
          Array.from(e.dataTransfer.items),
          Array.from(e.dataTransfer.files),
        );
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,.md,application/zip,text/markdown"
        className="hidden"
        disabled={disabled}
        // A programmatic .click() on these hidden inputs bubbles back up
        // to the zone's onClick, which would open the file picker on top
        // of whichever dialog was actually requested.
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          if (e.target.files) onFilesSelected(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      <input
        // React drops unknown non-string DOM attributes, so set the
        // non-standard directory-picker attributes imperatively.
        ref={(el) => {
          folderInputRef.current = el;
          if (el) {
            el.setAttribute("webkitdirectory", "");
            el.setAttribute("directory", "");
          }
        }}
        type="file"
        multiple
        className="hidden"
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          // The fallback folder picker delivers a flat FileList with
          // webkitRelativePath set (root folder name included).
          if (e.target.files && e.target.files.length > 0) {
            const withPaths = Array.from(e.target.files).map((f) => ({
              relativePath:
                (f as File & { webkitRelativePath?: string })
                  .webkitRelativePath || f.name,
              file: f,
            }));
            onFolderSelected(
              withPaths,
              withPaths[0].relativePath.split("/")[0] || "Folder",
            );
          }
          e.target.value = "";
        }}
      />
      {source ? (
        <div className="flex items-center justify-center gap-3">
          <SourceIcon className="h-8 w-8 text-primary" />
          <div className="text-left">
            <p className="font-medium">{source.label}</p>
            <p className="text-sm text-muted-foreground">{source.sizeLabel}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Drag and drop a .zip, a SKILL.md, or a folder
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="text-sm text-primary underline-offset-2 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) fileInputRef.current?.click();
              }}
            >
              Browse files
            </button>
            <span className="text-sm text-muted-foreground">·</span>
            <button
              type="button"
              className="text-sm text-primary underline-offset-2 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) void pickFolder();
              }}
            >
              Browse folder
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface SkillUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (skill: { id: string }) => void;
}

/**
 * Create a skill from a .zip, a bare SKILL.md, or an unzipped folder.
 *
 * A bare SKILL.md goes straight through the create endpoint (`skill_md`).
 * Multi-file sources go through the staged import flow: manifest →
 * server-presigned uploads into a staging prefix → finalize, where the server
 * parses the staged SKILL.md and creates the skill and its files atomically.
 * A name conflict at finalize retries with `name_override` — the staged files
 * are reused, nothing is re-uploaded.
 */
export const SkillUploadDialog: React.FC<SkillUploadDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<UploadSource | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    uploaded: number;
    total: number;
  } | null>(null);
  // A finalize-time name collision keeps the import id so "Keep both" only
  // re-finalizes under the suggested name — the staged files are reused.
  const [conflict, setConflict] = useState<{
    originalName: string;
    suggestedName: string;
    retryImportId: string | null;
  } | null>(null);

  const { data: existingSkills = [] } = useSkills({ enabled: open });

  const resetState = () => {
    setSource(null);
    setValidation(null);
    setUploadProgress(null);
    setIsValidating(false);
    setConflict(null);
  };

  const selectZip = useCallback(async (file: File) => {
    setSource({
      kind: "zip",
      label: file.name,
      sizeLabel: formatSize(file.size),
    });
    setValidation(null);
    setConflict(null);
    setIsValidating(true);
    try {
      const zip = await JSZip.loadAsync(await file.arrayBuffer());
      const zipEntries: { path: string; entry: JSZip.JSZipObject }[] = [];
      zip.forEach((p, entry) => {
        if (!entry.dir && !isOSJunkFile(p)) {
          zipEntries.push({ path: p.replace(/\\/g, "/"), entry });
        }
      });
      // JSZip doesn't expose sizes without decompressing; the archive is
      // small (≤50 MB total), so materialize blobs once and reuse them for
      // both validation and upload.
      const entries: UploadEntry[] = [];
      let skillMdText: string | null = null;
      const skillMdPath = findSkillMdPath(zipEntries.map((z) => z.path));
      for (const { path, entry } of zipEntries) {
        const blob = await entry.async("blob");
        entries.push({ path, size: blob.size, blob: async () => blob });
        if (path === skillMdPath) {
          skillMdText = await entry.async("text");
        }
      }
      setValidation(validateEntries(entries, skillMdText));
    } catch {
      setValidation({
        valid: false,
        errors: ["File is not a valid zip archive."],
        skillName: "",
        description: "",
        fileCount: 0,
        entries: [],
      });
    } finally {
      setIsValidating(false);
    }
  }, []);

  const selectMd = useCallback(async (file: File) => {
    setSource({
      kind: "md",
      label: file.name,
      sizeLabel: formatSize(file.size),
    });
    setValidation(null);
    setConflict(null);
    setIsValidating(true);
    try {
      const text = await file.text();
      setValidation(
        validateEntries(
          [{ path: "SKILL.md", size: file.size, blob: async () => file }],
          text,
        ),
      );
    } catch {
      setValidation({
        valid: false,
        errors: ["Failed to read the file."],
        skillName: "",
        description: "",
        fileCount: 0,
        entries: [],
      });
    } finally {
      setIsValidating(false);
    }
  }, []);

  const selectFolder = useCallback(
    async (files: { relativePath: string; file: File }[], label: string) => {
      const kept = files.filter((f) => !isOSJunkFile(f.relativePath));
      const totalBytes = kept.reduce((sum, f) => sum + f.file.size, 0);
      setSource({
        kind: "folder",
        label,
        sizeLabel: `${kept.length} files · ${formatSize(totalBytes)}`,
      });
      setValidation(null);
      setConflict(null);
      setIsValidating(true);
      try {
        const entries: UploadEntry[] = kept.map((f) => ({
          path: f.relativePath.replace(/\\/g, "/"),
          size: f.file.size,
          blob: async () => f.file,
        }));
        const skillMdPath = findSkillMdPath(entries.map((e) => e.path));
        const skillMdFile = kept.find(
          (f) => f.relativePath.replace(/\\/g, "/") === skillMdPath,
        );
        const skillMdText = skillMdFile ? await skillMdFile.file.text() : null;
        setValidation(validateEntries(entries, skillMdText));
      } finally {
        setIsValidating(false);
      }
    },
    [],
  );

  /** Single-file routing for the browse input and file-only drops. */
  const handleFiles = useCallback(
    (files: File[]) => {
      if (files.length !== 1) {
        toast.error("Select a single .zip or SKILL.md file, or a folder.");
        return;
      }
      const file = files[0];
      const name = file.name.toLowerCase();
      if (name.endsWith(".zip")) void selectZip(file);
      else if (name.endsWith(".md")) void selectMd(file);
      else toast.error("Select a .zip archive, a SKILL.md file, or a folder.");
    },
    [selectZip, selectMd],
  );

  const handleDropItems = useCallback(
    (items: DataTransferItem[], files: File[]) => {
      void (async () => {
        const tree = await traverseDataTransferItems(items);
        if (tree.files.length > 0) {
          if (tree.looseFiles.length > 0) {
            toast.error(
              "Drop a single folder on its own — loose files alongside it aren't part of the skill.",
            );
            return;
          }
          const label = tree.files[0].relativePath.split("/")[0] || "Folder";
          void selectFolder(tree.files, label);
          return;
        }
        handleFiles(files);
      })();
    },
    [selectFolder, handleFiles],
  );

  const navigateToSkill = (skill: { id: string }) => {
    if (onSuccess) {
      onSuccess(skill);
    } else {
      navigate(route(ROUTES.SKILLS.ROOT), {
        state: {
          rightPane: {
            pathname: route(ROUTES.SKILLS.SHOW, { id: skill.id }),
            search: "",
            hash: "",
            state: undefined,
            key: `pane-${Date.now()}-${Math.random()}`,
          },
        },
      });
    }
  };

  const finishSuccess = (
    skill: Skill,
    warnings?: string[],
    replaced?: boolean,
  ) => {
    queryClient.invalidateQueries({ queryKey: ["skills"] });
    if (replaced) {
      // The skill id survived the replace, so the pane's version-pinned file
      // queries must be refetched — same keys usePutSkill invalidates.
      queryClient.invalidateQueries({ queryKey: ["skill-files", skill.id] });
      queryClient.invalidateQueries({ queryKey: ["skill-file", skill.id] });
    }
    toast.success(`Skill "${skill.name}" ${replaced ? "replaced" : "created"}`);
    for (const warning of warnings ?? []) {
      toast(warning, { icon: "⚠️" });
    }
    onOpenChange(false);
    resetState();
    navigateToSkill(skill);
  };

  /** Finalize, or re-finalize after a collision (renamed copy or replace). */
  const finalize = async (
    importId: string,
    opts?: { nameOverride?: string; replace?: boolean },
  ) => {
    try {
      const { skill, warnings } = await orgAPI.skills.finalizeImport(importId, {
        name_override: opts?.nameOverride,
        replace: opts?.replace,
      });
      setConflict(null);
      finishSuccess(skill, warnings, opts?.replace);
    } catch (err) {
      if (isSkillNameTaken(err)) {
        const base = opts?.nameOverride ?? validation?.skillName ?? "skill";
        setConflict({
          originalName: base,
          suggestedName: generateUniqueSkillName(
            existingSkills.map((s) => s.name),
            base,
          ),
          retryImportId: importId,
        });
        return;
      }
      throw err;
    }
  };

  // Every mode — including a bare SKILL.md — goes through the staged import
  // flow, so over-length descriptions truncate with a warning and name
  // conflicts get the Keep-both dialog uniformly.
  const doUpload = async () => {
    if (!source || !validation?.valid) return;
    setIsUploading(true);
    try {
      const { import_id, files } = await orgAPI.skills.createImport({
        files: validation.entries.map((e) => ({
          path: e.path,
          size: e.size,
          content_type: mime.lookup(e.path) || undefined,
        })),
      });

      const byPath = new Map(validation.entries.map((e) => [e.path, e]));
      setUploadProgress({ uploaded: 0, total: files.length });
      for (let i = 0; i < files.length; i++) {
        const presigned = files[i];
        const entry = byPath.get(presigned.path);
        if (!entry) continue;
        await uploadPresignedSkillFile(
          {
            file_name: presigned.rel_path,
            method: presigned.method,
            url: presigned.url,
            field_values: presigned.field_values,
          },
          await entry.blob(),
          { contentType: mime.lookup(presigned.rel_path) || undefined },
        );
        setUploadProgress({ uploaded: i + 1, total: files.length });
      }

      await finalize(import_id);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to upload skill";
      toast.error(message);
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const handleUpload = async () => {
    if (!source || !validation?.valid || isUploading) return;
    await doUpload();
  };

  const canUpload =
    source && validation?.valid && !isUploading && !isValidating;

  return (
    <>
      <SkillNameConflictDialog
        open={conflict !== null}
        originalName={conflict?.originalName ?? ""}
        suggestedName={conflict?.suggestedName ?? ""}
        isPending={isUploading}
        onCancel={() => setConflict(null)}
        onKeepBoth={() => {
          if (!conflict?.retryImportId) return;
          const { retryImportId, suggestedName } = conflict;
          setConflict(null);
          setIsUploading(true);
          void finalize(retryImportId, { nameOverride: suggestedName })
            .catch((err) => {
              toast.error(
                err instanceof Error ? err.message : "Failed to create skill",
              );
            })
            .finally(() => setIsUploading(false));
        }}
        onReplaceContent={() => {
          if (!conflict?.retryImportId) return;
          const { retryImportId } = conflict;
          setConflict(null);
          setIsUploading(true);
          void finalize(retryImportId, { replace: true })
            .catch((err) => {
              toast.error(
                err instanceof Error ? err.message : "Failed to replace skill",
              );
            })
            .finally(() => setIsUploading(false));
        }}
      />
      <Dialog
        open={open}
        onOpenChange={(val) => {
          if (!isUploading) {
            onOpenChange(val);
            if (!val) resetState();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload a Skill</DialogTitle>
            <DialogDescription>
              Upload a .zip archive, a bare SKILL.md, or an unzipped folder. The
              skill's name and description come from the SKILL.md frontmatter.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <SkillUploadDropzone
              disabled={isUploading}
              source={source}
              onFilesSelected={handleFiles}
              onFolderSelected={(files, label) =>
                void selectFolder(files, label)
              }
              onDropItems={handleDropItems}
            />

            {isValidating && (
              <p className="text-sm text-muted-foreground animate-pulse">
                Checking the selection…
              </p>
            )}

            {validation && !isValidating && (
              <div className="space-y-3">
                {validation.errors.length > 0 && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-1.5">
                    {validation.errors.map((err, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 text-sm text-destructive"
                      >
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{err}</span>
                      </div>
                    ))}
                  </div>
                )}

                {validation.valid && (
                  <div className="rounded-md border border-lc-active/40 bg-lc-active/5 p-3 space-y-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-lc-active dark:text-lc-active">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      Ready to upload
                    </div>
                    <div className="text-xs text-muted-foreground pl-6 space-y-0.5">
                      <p>
                        <span className="font-medium text-foreground">
                          Name:
                        </span>{" "}
                        {validation.skillName || "(from SKILL.md)"}
                      </p>
                      <p>
                        <span className="font-medium text-foreground">
                          Description:
                        </span>{" "}
                        {validation.description}
                      </p>
                      <p>
                        <span className="font-medium text-foreground">
                          Files:
                        </span>{" "}
                        {validation.fileCount}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {uploadProgress && (
            <div className="space-y-2 pb-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Uploading files…</span>
                <span className="font-medium tabular-nums">
                  {uploadProgress.uploaded}/{uploadProgress.total}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
                  style={{
                    width: `${uploadProgress.total > 0 ? (uploadProgress.uploaded / uploadProgress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!canUpload}
              isLoading={isUploading}
            >
              Upload Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
